import {
  FormInstanceResponse,
  IFormDetailResponseField,
  IFormDetailResponseGroup,
  IRequestFormDetail,
  Section,
} from '@eredes/dfcore';

import { IAttachment } from '../_models/attachment.interface';
import { StateStyle } from '../_models/request-state.view-model';
import { RequestStatus } from '../_models/request-status.enum';

const REQUEST_TYPES = {
  PE: 'esclarecimento',
  PO: 'operação',
};

const CONFIG_BY_STATUS = {
  [RequestStatus.PENDING_RETAILER]: {
    style: {
      bgState: 'purple',
      colorState: 'white',
    },
    nextAction: 'Anular pedido',
    canUpdateRequest: true,
    displayForm: true,
  },
  [RequestStatus.SOLVED]: {
    style: {
      bgState: 'green',
      colorState: 'white',
    },
    nextAction: 'Reabrir',
    canUpdateRequest: false,
    displayForm: false,
  },
  [RequestStatus.CANCELED]: {
    style: {
      bgState: 'salmon',
      colorState: 'white',
    },
    nextAction: '',
    canUpdateRequest: false,
    displayForm: true,
  },
  [RequestStatus.SUBMITED]: {
    style: {
      bgState: 'greylight',
      colorState: 'white',
    },
    nextAction: '',
    canUpdateRequest: true,
    displayForm: true,
  },
  [RequestStatus.IN_PROGRESS]: {
    style: {
      bgState: 'blue',
      colorState: 'white',
    },
    nextAction: 'Anular pedido',
    canUpdateRequest: true,
    displayForm: true,
  },
};

/**
 * Helps manipulate and convert form data
 */
export class RequestFormHelper {
  /**
   * Create a object of type {@link T} from list of {@link Section} using
   * 'code' and 'valuesFilled' fields from each field of {@link sections}
   * @param sections list of {@link Section}
   * @returns
   */
  static convertSectionFieldsToFormObject<T>(sections: Section[]): T {
    const formObject = {} as T;
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        formObject[field.code] = RequestFormHelper.getValueFilled(field.valuesFilled);
      });
    });

    return formObject;
  }

  /**
   * Get the first string list value when there are items or return an empty string
   * @param valuesFilled
   * @returns
   */
  static getValueFilled(valuesFilled: string[]): string {
    if (valuesFilled && valuesFilled.length > 0) {
      return valuesFilled[0];
    }

    return '';
  }

  /**
   * Get style (background color and text color) by status name
   * @param statusName
   * @returns
   */
  static getStateStyleByStatusName(statusName: string): StateStyle {
    const state = CONFIG_BY_STATUS[statusName];

    return state && state.style
      ? state.style
      : {
          bgState: 'blue',
          colorState: 'white',
        };
  }

  /**
   * Get next action text by status name
   * @param statusName
   * @param validate callback function to validate with the required rule. Must return boolean
   * @returns
   */
  static getNextActionByStatusName(statusName: string, validate?: Function): string {
    const isValid = validate ? validate() : true;
    const state = CONFIG_BY_STATUS[statusName];

    return isValid && state ? state.nextAction : '';
  }

  /**
   * Checks if can update the request by the current status of the order
   * @param statusName
   * @param validate callback function to validate with the required rule. Must return boolean
   * @returns
   */
  static canUpdateRequestByStatusName(statusName: string, validate?: Function): boolean {
    const isValid = validate ? validate() : true;
    const state = CONFIG_BY_STATUS[statusName];

    return isValid && state ? state.canUpdateRequest : false;
  }

  /**
   * Checks whether to view the form according to the current status of the order
   * @param statusName
   * @param validate callback function to validate with the required rule. Must return boolean
   * @returns
   */
  static checkDisplayForm(statusName: string, validate?: Function): boolean {
    const isValid = validate ? validate() : true;
    const state = CONFIG_BY_STATUS[statusName];

    return isValid && state ? state.displayForm : false;
  }

  /**
   * Get alias for request type
   * @param value (esclarecimento / operação)
   * @returns (PE / PO)
   */
  static getRequestTypeAlias(value: string): string {
    const array = Object.keys(REQUEST_TYPES);
    const alias = array.find((item) => value.toLowerCase() === REQUEST_TYPES[item]);

    return alias ? alias : '';
  }

  /**
   * Prepares file content to send
   * @param file reference to file in the multi-file-Upload
   * @returns Promise to finish reader of file
   */
  static setReader(file: File): Promise<IAttachment> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function (e) {
        resolve({
          name: file.name,
          type: file.type,
          base64: reader.result,
          size: file.size,
          value: null,
          source: null,
        });
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Search field by code into section list
   * @param sections
   * @param code
   * @returns field value
   */
  static extractFieldByCode(sections: Section[], code: string) {
    let result = '';
    if (sections && sections.length) {
      sections.forEach((section: Section) => {
        const { fields } = section;
        if (fields && fields.length) {
          fields.forEach((field) => {
            if (field.code === code) {
              result = field.valuesFilled[0];
            }
          });
        }
      });
    }

    return result;
  }

  /**
   * Extract form type uuid from breadcrumbs
   * @param breadCrumbs
   * @returns string uuid
   */
  static extractFormTypeUuid(breadCrumbs: any[]): string {
    if (breadCrumbs && breadCrumbs.length > 0) {
      return breadCrumbs[breadCrumbs.length - 1].uuid;
    }

    return '';
  }

  /**
   * Checks whether the form of a parent form
   * @param form {@link FormInstanceResponse}
   * @returns true | false
   */
  static hasParentForm(form: IRequestFormDetail): boolean {
    return form && form.related && form.related.parent.length > 0;
  }

  /**
   * Extract field by uuid from groups
   * @param groups
   * @param uuid
   * @returns
   */
  static extractFieldByUuid(groups: IFormDetailResponseGroup[], uuid: string): string {
    let result = '';
    groups.forEach((group: IFormDetailResponseGroup) => {
      group.fields.forEach((field: IFormDetailResponseField) => {
        if (field.uuid === uuid) {
          result = field.value;
        }
      });
    });

    return result;
  }
}
